import * as React from "react";
import { useEffect, useState } from "react";
import { Box, Chip, Paper, SxProps, Theme, Typography } from "@mui/material";
import { ArrowForward, Check } from "@mui/icons-material";
import { translate } from "../../../../../translate/translate";
import { BaseService } from "../../../../../services/base-service";
import { URLS } from "../../../../../services/app-urls";
import { formatDate } from "../../../../../utils/format-date";
import {
  NpiChecklistOverview,
  NpiChecklistPartNumber,
  NpiChecklistPhase,
  NpiChecklistProgressSummary,
  NpiChecklistPhaseStatus,
  NpiChecklistStatusSummary,
} from "../../../../../models/npi/npi-checklist";
import { getChecklistPhaseColor } from "../constants/dashboard.constants";
import { cardSx } from "../styles/npi-dashboard.styles";

interface NpiChecklistOverviewProps {
  checklistId: number;
}

const phaseStatusChip: Record<NpiChecklistPhaseStatus, { label: string; color: string; backgroundColor: string }> = {
  [NpiChecklistPhaseStatus.COMPLETED]: { label: "phase_completed", color: "#15803D", backgroundColor: "#DCFCE7" },
  [NpiChecklistPhaseStatus.IN_PROGRESS]: { label: "phase_in_progress", color: "#1D4ED8", backgroundColor: "#DBEAFE" },
  [NpiChecklistPhaseStatus.PENDING]: { label: "phase_pending", color: "#64748B", backgroundColor: "#F1F5F9" },
};

const statusCards: { key: keyof NpiChecklistStatusSummary; label: string; color: string; legend?: string }[] = [
  { key: "complete", label: "checklist_complete", color: "#16A34A", legend: "checklist_legend_green" },
  { key: "on_going", label: "checklist_on_going", color: "#F59E0B", legend: "checklist_legend_yellow" },
  { key: "at_risk", label: "checklist_at_risk", color: "#F43F5E", legend: "checklist_legend_red" },
  { key: "not_applicable", label: "checklist_not_applicable", color: "#94A3B8" },
];

const sectionTitleSx = { fontSize: "14px", fontWeight: 600, color: "#1B1F27", textTransform: "uppercase" };
const infoLabelSx = { fontSize: "10px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.02em" };
const infoTitleSx = { fontSize: "16px", fontWeight: 700, color: "#1B1F27", lineHeight: 1.4 };

const progressCards = statusCards.filter((card) => card.legend);

const groupPartNumbersByBoard = (partNumbers: NpiChecklistPartNumber[]) =>
  partNumbers.reduce<Record<string, string[]>>((groups, { board, part_number }) => {
    const key = board || "-";
    (groups[key] ??= []).push(part_number);
    return groups;
  }, {});

const formatPhaseDate = (value: string | null) => (value ? formatDate(value) : "-");

const PhaseTimeline = ({ phases }: { phases: NpiChecklistPhase[] }) => (
  <Box sx={{ display: "flex", mt: 3 }}>
    {[...phases].sort((a, b) => a.order - b.order).map((phase, index) => {
      const color = getChecklistPhaseColor(index);
      const chip = phaseStatusChip[phase.status];
      return (
        <Box key={phase.id} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
            <Box sx={{ flex: 1, height: "2px", backgroundColor: index === 0 ? "transparent" : color }} />
            <Box
              sx={{
                position: "relative",
                width: 32,
                flexShrink: 0,
                height: 32,
                borderRadius: "50%",
                backgroundColor: color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              {phase.status === NpiChecklistPhaseStatus.COMPLETED ? (
                <Check sx={{ color: "#fff", fontSize: 18 }} />
              ) : (
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#fff" }} />
              )}
            </Box>
            <Box sx={{ flex: 1, height: "2px", backgroundColor: index === phases.length - 1 ? "transparent" : color }} />
          </Box>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color, mt: 1, textTransform: "uppercase" }}>{phase.name}</Typography>
          <Chip
            size="small"
            label={translate.t(chip.label)}
            sx={{
              height: 20,
              borderRadius: "4px",
              fontSize: "11px",
              fontWeight: 600,
              color: chip.color,
              backgroundColor: chip.backgroundColor,
              "& .MuiChip-label": { px: 1 },
            }}
          />
          <Typography sx={{ fontSize: "13px", color: "#45485F", display: "flex", alignItems: "center", gap: 0.5 }}>
            {formatPhaseDate(phase.start_date)}
            <ArrowForward sx={{ fontSize: 14 }} />
            {formatPhaseDate(phase.end_date)}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const StatusCard = ({ label, color, value, percent, sx }: { label: string; color: string; value: number; percent: number; sx?: SxProps<Theme>}) => (
  <Paper variant="outlined" sx={[{ ...cardSx, flex: 1, minWidth: 150 }, ...(Array.isArray(sx) ? sx : [sx])]}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography sx={{ fontSize: "12px", fontWeight: 600, color, textTransform: "uppercase" }}>{translate.t(label)}</Typography>
      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color }} />
    </Box>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", my: 1.5 }}>
      <Typography sx={{ fontSize: "32px", fontWeight: 600, color: "#1B1F27", lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: "13px", fontWeight: 600, color }}>{percent}%</Typography>
    </Box>
    <Box sx={{ height: 6, borderRadius: 3, backgroundColor: "#F1F5F9", overflow: "hidden" }}>
      <Box sx={{ width: `${value > 0 ? Math.max(percent, 2) : 0}%`, height: "100%", backgroundColor: color, borderRadius: 3 }} />
    </Box>
  </Paper>
);

const ProgressDonut = ({ progress }: { progress: NpiChecklistProgressSummary }) => {
  const size = 160;
  const strokeWidth = 22;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const getProgress = (key: keyof NpiChecklistStatusSummary) => progress[key as keyof NpiChecklistProgressSummary];
  const segments = progressCards.filter((card) => getProgress(card.key) > 0);
  let offset = 0;

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, mt: 2 }}>
      <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={strokeWidth} />
          {segments.map((card) => {
            const length = (getProgress(card.key) / 100) * circumference;
            const circle = (
              <circle
                key={card.key}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={card.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${length} ${circumference - length}`}
                strokeDashoffset={-offset}
              />
            );
            offset += length;
            return circle;
          })}
        </svg>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}>
          <Typography sx={{ fontSize: "32px", fontWeight: 700, color: "#1B1F27", lineHeight: 1 }}>
            {progress.complete}%
          </Typography>
          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", mt: 0.5 }}>
            {translate.t("checklist_completed_label")}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {segments.map((card) => (
          <Box key={card.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: card.color }} />
            <Typography sx={{ fontSize: "13px", color: "#45485F" }}>
              {translate.t(card.legend!)} ({getProgress(card.key)}%)
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Typography
    component="div"
    sx={{ fontSize: "13px", color: "#64748B", lineHeight: 1.6, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
    {label}:
    {children}
  </Typography>
);

const InfoValue = ({ children }: { children: React.ReactNode }) => (
  <Box component="span" sx={{ width: "auto", fontWeight: 600, color: "#1B1F27" }}>
    {children}
  </Box>
);

const PartNumberChips = ({ partNumbers }: { partNumbers: string[] }) => (
  <>
    {partNumbers.map((partNumber) => (
      <Chip
        key={partNumber}
        size="small"
        label={partNumber}
        sx={{
          height: 20,
          borderRadius: "10px",
          fontSize: "10px",
          fontWeight: 600,
          color: "#2563EB",
          backgroundColor: "#DBEAFE",
          "& .MuiChip-label": { px: 1 },
        }}
      />
    ))}
  </>
);

const InfoCard = ({ title, children, sx }: { title: string; children: React.ReactNode; sx?: SxProps<Theme> }) => (
  <Paper
    variant="outlined"
    sx={[
      { ...cardSx, py: 1.5, flex: 1, minWidth: 260, display: "flex", flexDirection: "column", gap: 0.25 },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}>
    <Typography sx={{ ...infoLabelSx, pb: 0.75, mb: 1, borderBottom: "1px solid #E2E8F0" }}>{translate.t(title)}</Typography>
    {children}
  </Paper>
);

const NpiChecklistOverviewComponent = ({ checklistId }: NpiChecklistOverviewProps) => {
  const [overview, setOverview] = useState<NpiChecklistOverview | null>(null);

  useEffect(() => {
    const checklistService = new BaseService<NpiChecklistOverview>(URLS.NPI_CHECKLIST);
    checklistService.getFromDetailRoute(checklistId, "overview").then((response) => setOverview(response.data));
  }, [checklistId]);

  if (!overview) {
    return null;
  }

  const summary: NpiChecklistStatusSummary = {
    complete: overview.total_items_completed,
    on_going: overview.total_items_on_going,
    at_risk: overview.total_items_pending,
    not_applicable: overview.total_items_not_applied,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper variant="outlined" sx={cardSx}>
        <Typography sx={sectionTitleSx}>{translate.t("npi_transfer_phases")}</Typography>
        <PhaseTimeline phases={overview.phases} />
      </Paper>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Paper variant="outlined" sx={{ ...cardSx, flex: 2, minWidth: 320 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography sx={sectionTitleSx}>{translate.t("checklist_status_overview")}</Typography>
            <Typography sx={{ fontSize: "13px", color: "#64748B" }}>
              {translate.t("total")}: <InfoValue>{overview.total_items}</InfoValue> {translate.t("mapped_requirements")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {statusCards.map((card) => (
              <StatusCard
                sx={{ background: "#F9FAFC" }}
                key={card.key}
                label={card.label}
                color={card.color}
                value={summary[card.key]}
                percent={overview.items_percent[card.key]}
              />
            ))}
          </Box>
        </Paper>
        <Paper variant="outlined" sx={{ ...cardSx, flex: 1, minWidth: 320 }}>
          <Typography sx={sectionTitleSx}>{translate.t("npi_transfer_progress")}</Typography>
          <ProgressDonut progress={overview.progress_percent} />
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ ...cardSx, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <InfoCard title="oem_client" sx={{ background: "#F9FAFC" }}>
          <Typography sx={infoTitleSx}>{overview.oem || "-"}</Typography>
          <InfoRow label={translate.t("product")}>
            <InfoValue>{overview.project.name}</InfoValue>
          </InfoRow>
          <InfoRow label={translate.t("kickoff")}>
            <InfoValue>{overview.kick_off_date ? formatDate(overview.kick_off_date) : "-"}</InfoValue>
          </InfoRow>
        </InfoCard>
        <InfoCard title="odm_manufacturer" sx={{ background: "#F9FAFC" }}>
          <Typography sx={infoTitleSx}>{overview.odm || "-"}</Typography>
          <InfoRow label={translate.t("pcba_rev")}>
            <InfoValue>{overview.pcba_rev || "-"}</InfoValue>
          </InfoRow>
        </InfoCard>
        <InfoCard title="part_numbers" sx={{ background: "#F9FAFC" }}>
          {Object.entries(groupPartNumbersByBoard(overview.part_numbers)).map(([board, partNumbers]) => (
            <InfoRow key={board} label={board}>
              <PartNumberChips partNumbers={partNumbers} />
            </InfoRow>
          ))}
        </InfoCard>
      </Paper>
    </Box>
  );
};

export default NpiChecklistOverviewComponent;
